import React, { useState, useEffect } from "react";
import styles from "./FlujoCaja.module.scss";

const today = new Date().toISOString().slice(0, 10);

const parseDecimal = (val) => {
  if (!val) return NaN;
  return parseFloat(String(val).replace(",", "."));
};

const CambioMonedaModal = ({ show, onClose, onSave, normalizedPais }) => {
  const monedaLocal = normalizedPais === "Chile" ? "CLP" : "PEN";
  const monedas = normalizedPais === "Chile" ? ["CLP", "USD", "UF"] : ["PEN", "USD"];

  const [fecha, setFecha] = useState(today);
  const [monedaOrigen, setMonedaOrigen] = useState("USD");
  const [montoOrigen, setMontoOrigen] = useState("");
  const [tipoCambio, setTipoCambio] = useState("");
  const [monedaDestino, setMonedaDestino] = useState(monedaLocal);
  const [montoDestino, setMontoDestino] = useState("");
  const [saving, setSaving] = useState(false);
  // Rastrea cuál campo editó el usuario para recalcular los otros
  const [lastEdited, setLastEdited] = useState(null);

  // Auto-swap destino cuando cambia origen para evitar origen === destino
  useEffect(() => {
    if (monedaOrigen === monedaDestino) {
      setMonedaDestino(monedaOrigen === "USD" ? monedaLocal : "USD");
    }
  }, [monedaOrigen, monedaDestino, monedaLocal]);

  // Recalcular según qué campo editó el usuario
  useEffect(() => {
    const origen = parseDecimal(montoOrigen);
    const tc = parseDecimal(tipoCambio);
    const destino = parseDecimal(montoDestino);

    if (lastEdited === "tipoCambio" && !isNaN(origen) && !isNaN(tc)) {
      setMontoDestino((origen * tc).toFixed(2));
    } else if (lastEdited === "montoDestino" && !isNaN(origen) && origen > 0 && !isNaN(destino)) {
      setTipoCambio((destino / origen).toFixed(4));
    } else if (lastEdited === "montoOrigen" && !isNaN(tc) && !isNaN(origen)) {
      setMontoDestino((origen * tc).toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [montoOrigen, tipoCambio, montoDestino, lastEdited]);

  const canSave =
    fecha &&
    monedaOrigen &&
    monedaDestino &&
    monedaOrigen !== monedaDestino &&
    montoOrigen &&
    tipoCambio &&
    montoDestino;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave({
        fecha,
        monedaOrigen,
        montoOrigen: parseDecimal(montoOrigen),
        monedaDestino,
        montoDestino: parseDecimal(montoDestino),
        tipoCambio: parseDecimal(tipoCambio),
      });
      // Reset
      setFecha(today);
      setMonedaOrigen("USD");
      setMontoOrigen("");
      setTipoCambio("");
      setMontoDestino("");
      setMonedaDestino(monedaLocal);
      setLastEdited(null);
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const tcLabel = `${monedaDestino} por 1 ${monedaOrigen}`;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContentSmall} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h5>Cambio de Moneda</h5>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer" }}
          >
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          {/* Fecha */}
          <div className={styles.formGroup} style={{ marginBottom: 16 }}>
            <label>Fecha</label>
            <input type="date" className="form-control" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>

          {/* Origen */}
          <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 10, marginBottom: 12 }}>
            <div className={styles.formGroup}>
              <label>Moneda origen</label>
              <select className="form-select" value={monedaOrigen} onChange={(e) => setMonedaOrigen(e.target.value)}>
                {monedas.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Monto origen</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                placeholder="0"
                value={montoOrigen}
                onChange={(e) => {
                  setMontoOrigen(e.target.value);
                  setLastEdited("montoOrigen");
                }}
              />
            </div>
          </div>

          {/* Tipo de cambio */}
          <div className={styles.formGroup} style={{ marginBottom: 12 }}>
            <label>
              Tipo de cambio <span style={{ fontWeight: 400, color: "#888", fontSize: "0.8rem" }}>({tcLabel})</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              className="form-control"
              placeholder="0"
              value={tipoCambio}
              onChange={(e) => {
                setTipoCambio(e.target.value);
                setLastEdited("tipoCambio");
              }}
            />
          </div>

          {/* Destino */}
          <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 10, marginBottom: 4 }}>
            <div className={styles.formGroup}>
              <label>Moneda destino</label>
              <select className="form-select" value={monedaDestino} onChange={(e) => setMonedaDestino(e.target.value)}>
                {monedas
                  .filter((m) => m !== monedaOrigen)
                  .map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Monto destino</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                placeholder="0"
                value={montoDestino}
                onChange={(e) => {
                  setMontoDestino(e.target.value);
                  setLastEdited("montoDestino");
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={!canSave || saving}>
            {saving ? "Guardando..." : "Registrar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CambioMonedaModal;
